"use client";

import Image from "next/image";
import { cn } from "@ui/lib";

function isLightThemedLogo(logoUrl: string | null | undefined): boolean {
  if (!logoUrl) return false;
  return logoUrl.includes("theme/light") || logoUrl.includes("/light/");
}

const SPYGLASSES_LOGO = "/images/logo/spyglasses_logotype.svg";
const SPYGLASSES_NAME = "Spyglasses";

export interface ReportHeaderProps {
  /** Report type label shown above the title (e.g. "AI VISIBILITY REPORT") */
  reportTypeLabel: string;
  /** Main title (usually the company name) */
  title: string;
  /** Subtitle shown below the title */
  subtitle: string;
  /** Agency branding data — when present, shows agency logo + "Prepared by [agency]" instead of Spyglasses */
  agencyBranding?: {
    name: string;
    logo?: string | null;
  } | null;
  /** Accent color for the report type label (hex, with or without #) */
  accentColor?: string;
  /** Optional content rendered on the right side of the header (e.g. share button) */
  actions?: React.ReactNode;
}

export function ReportHeader({
  reportTypeLabel,
  title,
  subtitle,
  agencyBranding,
  accentColor,
  actions,
}: ReportHeaderProps) {
  const normalizedAccent = accentColor
    ? accentColor.startsWith("#") ? accentColor : `#${accentColor}`
    : undefined;

  const hasAgencyLogo = !!agencyBranding?.logo;
  const logoSrc = hasAgencyLogo ? agencyBranding!.logo! : SPYGLASSES_LOGO;
  const isSpyglassesLogo = !hasAgencyLogo;
  const preparedByName = agencyBranding?.name || SPYGLASSES_NAME;

  return (
    <header className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div
              className={cn(
                "rounded-lg border border-gray-200 flex items-center justify-center shrink-0",
                isSpyglassesLogo
                  ? "h-14 w-[180px] p-2.5 bg-white"
                  : "w-12 h-12 overflow-hidden",
                !isSpyglassesLogo &&
                  (isLightThemedLogo(logoSrc)
                    ? "bg-gray-800"
                    : "bg-white")
              )}
            >
              <Image
                src={logoSrc}
                alt={isSpyglassesLogo ? SPYGLASSES_NAME : agencyBranding!.name}
                width={isSpyglassesLogo ? 160 : 40}
                height={isSpyglassesLogo ? 56 : 40}
                className="object-contain size-full max-w-full max-h-full"
              />
            </div>
            <div>
              <p
                className={cn(
                  "text-sm font-medium uppercase tracking-wide",
                  !normalizedAccent && "text-primary"
                )}
                style={normalizedAccent ? { color: normalizedAccent } : undefined}
              >
                {reportTypeLabel}
              </p>
              <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
                {title}
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Prepared by {preparedByName}
              </p>
            </div>
          </div>
          {actions && <div className="flex-shrink-0">{actions}</div>}
        </div>
        <p className="mt-2 text-lg text-gray-600 max-w-3xl">{subtitle}</p>
      </div>
    </header>
  );
}
